import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { aleaStepperStyles } from './AleaStepperStyles';

export interface StepperStep {
  /** Semantic step id for translation keys (e.g. toStep1–toStep4), not display order */
  id: number;
  title: string;
  isCompleted?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
}

interface AleaStepperProps {
  steps: StepperStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  isStepClickable?: (stepIndex: number) => boolean;
}

const AleaStepper: React.FC<AleaStepperProps> = ({
  steps,
  currentStep,
  onStepClick,
  isStepClickable,
}) => {
  // Track the highest step the user has reached to persist completed visuals when navigating backwards
  const maxVisitedStepRef = React.useRef<number>(currentStep);
  if (currentStep > maxVisitedStepRef.current) {
    maxVisitedStepRef.current = currentStep;
  }

  const getStepStatus = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (step?.isCompleted) return 'completed';
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'active';
    // If user has previously visited this step (greater than current but <= max visited), keep it completed
    if (stepIndex <= maxVisitedStepRef.current && !step?.isDisabled) return 'completed';
    return 'disabled';
  };

  return (
    <Flex sx={aleaStepperStyles.container}>
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isClickable = onStepClick && (
          isStepClickable ? isStepClickable(index) : 
          (index <= 1 || status === 'completed' || status === 'active')
        );

        return (
          <React.Fragment key={`${step.id}-${index}`}>
            <Flex
              sx={aleaStepperStyles.stepContainer}
              onClick={isClickable ? () => onStepClick(index) : undefined}
              cursor={isClickable ? 'pointer' : 'default'}
            >
              {/* Step Circle */}
              <Box
                sx={{
                  ...aleaStepperStyles.stepCircle.base,
                  ...aleaStepperStyles.stepCircle[status],
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                }}
              >
                <Text
                  sx={{
                    ...aleaStepperStyles.stepNumber.base,
                    ...aleaStepperStyles.stepNumber[status],
                  }}
                >
                  {index + 1}
                </Text>
              </Box>

              {/* Step Title */}
              <Text
                sx={{
                  ...aleaStepperStyles.stepTitle.base,
                  ...aleaStepperStyles.stepTitle[status],
                }}
              >
                {step.title}
              </Text>
            </Flex>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <Box
                sx={{
                  ...aleaStepperStyles.connector.base,
                  ...aleaStepperStyles.connector[(index < currentStep || index < maxVisitedStepRef.current) ? 'completed' : 'disabled'],
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};

export default AleaStepper;